
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { captureMessage } from '@@/bits/error_tracker'
import { isAppUsing, isDebugMode } from '@@/bits/flip'
import { getSearchParam } from '@@/bits/location'
import { PiniaMapAction, PiniaMapState } from '@@/bits/pinia'
import { observeSizeChange } from '@@/bits/resize_observer'
import { Component, Vue, defineAsyncComponent } from '@@/bits/vue'
import { initializeZoomSdk } from '@@/bits/zoom'
import { useCommandsStore } from '@@/pinia/commands'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAIChatStore } from '@@/pinia/surface_ai_chat_store'
import { useSurfaceGradingPanelStore } from '@@/pinia/surface_grading_panel'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Wall } from '@@/types'
import SkipToContentLink from '@@/vuecomponents/SkipToContentLink.vue'
import SurfaceOffline from '@@/vuecomponents/surface_offline.vue'
import { useFullscreen } from '@@/vuecomposables/fullscreen'
import { useDragAndDropFromExternalMonitor } from '@@/vuecomposables/useDragAndDropFromExternalMonitor'
import PageStylingMixin from '@@/vuemixins/page_styling_mixin'
import PresenceMixin from '@@/vuemixins/presence_mixin'
import { processedUrl } from '@padlet/vivaldi-client'
import tinykeys from 'tinykeys'

const mixins: any[] = [PageStylingMixin, PresenceMixin]

@Component({
  components: {
    // Don't lazy load SurfaceOffline. Otherwise, if the internet disconnects, we need to show this component,
    // but we can't show it because we can't fetch it.
    SurfaceOffline,
    SkipToContentLink,
    SurfaceContainer: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainer.vue')),
    WhiteboardContainer: defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardContainer.vue')),
    GlobalSnackbar: defineAsyncComponent(() => import('@@/vuecomponents/GlobalSnackbar.vue')),
    GlobalMentionAutocomplete: defineAsyncComponent(() => import('@@/vuecomponents/GlobalMentionAutocomplete.vue')),
    SurfaceDragAndDropOverlay: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceDragAndDropOverlay.vue')),
    SurfaceSlideshow: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSlideshow.vue')),
    CommandDialog: defineAsyncComponent(() => import('@@/vuecomponents/CommandDialog.vue')),
    SurfaceExportsDocumentStatus: defineAsyncComponent(
      () => import('@@/vuecomponents/SurfaceExportsDocumentStatus.vue'),
    ),
    SurfaceOnboardingPanel: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceOnboardingPanel.vue')),
    SurfaceOnboardingPanelMobile: defineAsyncComponent(
      () => import('@@/vuecomponents/SurfaceOnboardingPanelMobile.vue'),
    ),

    SurfaceGradingPanel: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGradingPanel.vue')),
    SurfaceGradingPanelMobile: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGradingPanelMobile.vue')),
    SurfaceOnboardingGuidedPanel: defineAsyncComponent(
      () => import('@@/vuecomponents/SurfaceOnboardingGuidedPanel.vue'),
    ),
    SurfaceChangeGuestModal: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceChangeGuestModal.vue')),
    SurfaceTransferWallModal: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceTransferWallModal.vue')),
    SurfaceGuestIdModal: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGuestIdModal.vue')),
    SurfaceGuestIdDrawer: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGuestIdDrawer.vue')),
    SurfaceAIChatPanel: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAIChatPanel.vue')),
    SurfaceOnboardingDemoPadletLanding: defineAsyncComponent(
      () => import('@@/vuecomponents/SurfaceOnboardingDemoPadletLanding.vue'),
    ),
  },
  mixins,
  setup() {
    useDragAndDropFromExternalMonitor()
  },
})
export default class SurfaceApp extends Vue {
  $refs!: {
    surfaceMobileSidePanel: Vue & { $el: HTMLElement }
  }

  dir = dir
  isAppUsing = isAppUsing
  device = device

  @PiniaMapState(useCommandsStore) xCommandDialog!: string
  @PiniaMapAction(useCommandsStore) showCommandDialog!: () => void
  @PiniaMapState(useSurfaceOnboardingPanelStore) xOnboardingPanel!: boolean
  @PiniaMapState(useSurfaceStore) isOnline!: boolean
  @PiniaMapState(useSurfaceStore) isGeneratingPDF!: boolean
  @PiniaMapState(useSurfaceStore) wallId!: string
  @PiniaMapState(useSurfaceStore) isSlideshow!: string
  @PiniaMapState(useSurfaceGradingPanelStore) xSurfaceGradingPanel!: boolean
  @PiniaMapState(useSurfaceStore) xTransferWallDialog!: ReturnType<typeof useSurfaceStore>['xTransferWallDialog']
  @PiniaMapState(useSurfaceStore) wallAttributes!: Partial<Wall>
  @PiniaMapState(useWindowSizeStore) isSmallerThanTabletPortrait!: boolean
  @PiniaMapState(useWindowSizeStore) isSmallerThanDesktop!: boolean
  @PiniaMapState(useSurfaceStore) isWhiteboard!: boolean
  @PiniaMapState(useSurfaceAIChatStore) xSurfaceAIChatPanel!: boolean
  @PiniaMapState(useSurfacePermissionsStore) canIPost!: boolean
  @PiniaMapState(useSurfacePermissionsStore) amIRegistered!: boolean
  @PiniaMapState(useSurfaceStore) hasSidePanelOutsideSurface!: boolean
  @PiniaMapState(useSurfaceStore) isSubmissionRequest!: boolean
  @PiniaMapState(useSurfaceStore) isScreenshotMode!: boolean
  @PiniaMapState(useSurfaceStore) isEmbedded!: boolean
  @PiniaMapState(useSurfaceStore) isSidePanelRounded!: boolean
  @PiniaMapState(useSurfaceGuestStore) xGuestIdModal!: boolean
  @PiniaMapState(useSurfaceGuestStore) xGuestIdCard!: boolean
  @PiniaMapState(useSurfaceGuestStore) xChangeGuestModal!: boolean
  @PiniaMapState(useSurfaceOnboardingDemoPadletPanelStore) isDemoPadletPanelMobile!: boolean
  @PiniaMapState(useSurfaceOnboardingDemoPadletPanelStore) isDemoPadletPanelDesktop!: boolean
  @PiniaMapState(useSurfaceOnboardingDemoPadletPanelStore) isDemoPadletPanel!: boolean
  @PiniaMapState(useSurfaceOnboardingDemoPadletPanelStore) isLandingStep!: boolean

  removeKeyboardShortcuts = () => {}
  async configureZoomApp() {
    const zoomSdk = (await import('@zoom/appssdk')).default
    await initializeZoomSdk()
    return zoomSdk
  }

  created() {
    useFullscreen()
  }

  get isDemoPadletLandingStep() {
    return this.isDemoPadletPanelDesktop && this.isLandingStep
  }

  beforeMount() {
    if (isAppUsing('fullClientSurface')) {
      requestAnimationFrame(() => {
        this.updateTitle()
        this.initializeFavicon()
        this.insertPrintNotificationElement()
        const mobileAppVersion = Number(document.body.getAttribute('ww-variant-version'))
        if (!this.isSlideshow && !this.isGeneratingPDF && !(device.app && mobileAppVersion >= 191)) {
          this.updatePageBackground()
        }
        this.updateBodyFontAttribute()
        this.updateBodyColorSchemeAttribute()
        this.updateDataLayoutAttribute()
      })
    }
  }

  updateTitle(): void {
    const wallTitle = this.wallAttributes?.title ?? this.__('Padlet') // Fallback
    if (this.isSlideshow) {
      document.title = `Slideshow | ${wallTitle}`
      return
    }
    if (this.isGeneratingPDF) {
      document.title = this.__('Generating PDF...')
      return
    }
    document.title = wallTitle
  }

  updatePageBackground(): void {
    const wallBackground = this.wallAttributes.background
    if (wallBackground == null) return
    const effect = wallBackground.effect === 'blur' ? 'blur:10' : undefined
    const imageUrl = processedUrl(wallBackground.url, {
      width: wallBackground.width,
      height: wallBackground.height,
      effect,
    })
    const image = new Image()
    image.src = imageUrl
    image.onload = () => {
      const bodyTag = document.body
      bodyTag.style.backgroundSize = wallBackground.fill === 'cover' ? 'cover' : 'auto'
      bodyTag.style.backgroundImage = `url('${imageUrl}')`
      // We unset the html root element's background image since
      // in full_client_surface/show.html.erb, we set it due to the html body not being rendered yet
      const htmlTag = document.documentElement
      htmlTag.style.backgroundImage = ''
    }
  }

  initializeFavicon(): void {
    const fragment = document.createDocumentFragment()
    if (this.isSlideshow) {
      const iconLinkTag = document.createElement('link')
      iconLinkTag.setAttribute('rel', 'icon')
      iconLinkTag.setAttribute('href', `/slideshow/favicon.ico`)
      iconLinkTag.setAttribute('sizes', '32x32')
      fragment.appendChild(iconLinkTag)
      const appleIconLinkTag = document.createElement('link')
      appleIconLinkTag.setAttribute('rel', 'apple-touch-icon')
      appleIconLinkTag.setAttribute('href', `/slideshow/apple-touch-icon.png`)
      appleIconLinkTag.setAttribute('sizes', '180x180')
      fragment.appendChild(appleIconLinkTag)
      const manifestLinkTag = document.createElement('link')
      manifestLinkTag.setAttribute('rel', 'manifest')
      manifestLinkTag.setAttribute('href', `/slideshow/site.webmanifest`)
      fragment.appendChild(manifestLinkTag)
      document.head.appendChild(fragment)
      return
    }
    if (this.wallAttributes.portrait !== undefined && this.wallAttributes.portrait !== '') {
      const iconLinkTag = document.createElement('link')
      iconLinkTag.setAttribute('rel', 'icon')
      const iconUrl = processedUrl(this.wallAttributes.portrait, {
        width: 64,
        height: 64,
        preset: 'icon',
      })
      iconLinkTag.setAttribute('href', iconUrl)
      fragment.appendChild(iconLinkTag)
      const appleIconLinkTag = document.createElement('link')
      appleIconLinkTag.setAttribute('rel', 'apple-touch-icon')
      const appleIconUrl = processedUrl(this.wallAttributes.portrait, {
        width: 180,
        height: 180,
        preset: 'icon',
      })
      appleIconLinkTag.setAttribute('href', appleIconUrl)
      appleIconLinkTag.setAttribute('sizes', '180x180')
      fragment.appendChild(appleIconLinkTag)
      document.head.appendChild(fragment)
      return
    }
    const iconLinkTag = document.createElement('link')
    iconLinkTag.setAttribute('rel', 'icon')
    iconLinkTag.setAttribute('href', 'https://padlet.net/favicon.ico')
    iconLinkTag.setAttribute('sizes', '32x32')
    fragment.appendChild(iconLinkTag)
    const iconSvgLinkTag = document.createElement('link')
    iconSvgLinkTag.setAttribute('rel', 'icon')
    iconSvgLinkTag.setAttribute('type', 'image/svg+xml')
    iconSvgLinkTag.setAttribute('href', 'https://padlet.net/favicon.svg')
    fragment.appendChild(iconSvgLinkTag)
    const appleIconLinkTag = document.createElement('link')
    appleIconLinkTag.setAttribute('rel', 'apple-touch-icon')
    appleIconLinkTag.setAttribute('href', 'https://padlet.net/apple-touch-icon.png')
    appleIconLinkTag.setAttribute('sizes', '180x180')
    fragment.appendChild(appleIconLinkTag)
    document.head.appendChild(fragment)
  }

  insertPrintNotificationElement(): void {
    const printNotificationElement = document.createElement('div')
    const appElement = document.getElementById('app')
    if (appElement === null) return
    printNotificationElement.setAttribute('id', 'print-notification')
    printNotificationElement.setAttribute(
      'title',
      this.__('To print this page properly, go to the "Share" panel and select "Print" under the "Export" section.'),
    )
    document.body.insertBefore(printNotificationElement, appElement)
  }

  updateBodyFontAttribute(): void {
    const bodyTag = document.body
    if (this.wallAttributes.font_id === undefined) return
    bodyTag.setAttribute('data-font', this.wallAttributes.font_id.toString())
  }

  updateBodyColorSchemeAttribute(): void {
    const bodyTag = document.body
    if (this.wallAttributes.color_scheme === undefined) return
    bodyTag.setAttribute('data-color-scheme', this.wallAttributes.color_scheme.toString())
  }

  updateDataLayoutAttribute(): void {
    const htmlTag = document.documentElement
    if (this.wallAttributes.viz === undefined) return
    htmlTag.setAttribute('data-layout', this.wallAttributes.viz.toString())
  }

  surfaceMobileSidePanelHeight = '0px'
  async mounted() {
    if (device.zoomApp) {
      const zoomSdk = await this.configureZoomApp()
      zoomSdk.expandApp({ action: 'expand' })
    }
    if (isDebugMode || isAppUsing('showCommandPalette')) {
      this.removeKeyboardShortcuts = tinykeys(document.documentElement as HTMLElement, {
        '$mod+KeyK': this.showCommandDialog,
      })
    }

    if (
      isAppUsing('anonymousAttribution') &&
      !this.amIRegistered &&
      !this.isSubmissionRequest &&
      !device.app &&
      !this.isEmbedded &&
      !this.isScreenshotMode
    ) {
      this.installTurnstile()
    }

    if (getSearchParam('grading_panel_open') === 'true') {
      this.$nextTick(() => {
        useSurfaceGradingPanelStore().showSurfaceGradingPanel()
      })
    }
  }

  async installTurnstile() {
    const loadInvisibleTurnstile = (await import('@@/bits/cloudflare_turnstile')).loadInvisibleTurnstile

    loadInvisibleTurnstile({ action: 'surface' }).then((widgetId) => {
      if (widgetId != null) {
        const surfaceGuestStore = useSurfaceGuestStore()
        surfaceGuestStore.setCloudflareTurnstileWidgetId(widgetId)
      } else {
        captureMessage('Failed to load Cloudflare Turnstile')
      }
    })
  }

  handleMobileSidePanelMounted(): void {
    const htmlElement = this.$refs.surfaceMobileSidePanel?.$el
    if (htmlElement === undefined) return
    this.surfaceMobileSidePanelHeight = `${htmlElement.clientHeight}px`
    observeSizeChange(htmlElement, () => {
      if (htmlElement === undefined) return
      this.surfaceMobileSidePanelHeight = `${htmlElement.clientHeight}px`
    })
  }

  beforeDestroy(): void {
    if (isDebugMode || isAppUsing('showCommandPalette')) {
      this.removeKeyboardShortcuts()
    }
  }
}
